import { MatterType } from './matterService'
import { MatterDocument } from './documentService'
import { API_BASE_URL } from '../config'

// Public: the client never logs in, so none of these attach a bearer token —
// the unguessable link token in the URL is the only credential (durable,
// unlike client-invites' single-use id — see backend/src/index.ts's
// resolveUploadLinkToken).

export interface UploadLinkStatus {
  valid: boolean;
  matterType: MatterType;
  propertyAddress: string;
}

export const getUploadLinkStatus = async (token: string): Promise<UploadLinkStatus> => {
  const response = await fetch(`${API_BASE_URL}/matter-upload-links/${token}`)
  const data = await response.json()

  if (!response.ok) {
    throw new Error(data?.error || `This link is not valid (${response.status})`)
  }

  return data
}

export const listUploadLinkDocuments = async (token: string): Promise<MatterDocument[]> => {
  const response = await fetch(`${API_BASE_URL}/matter-upload-links/${token}/documents`)
  const data = await response.json()

  if (!response.ok) {
    throw new Error(data?.error || `Failed to list documents (${response.status})`)
  }

  return data.documents || []
}

const uploadFileToS3 = async (uploadUrl: string, file: File): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  })

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`)
  }
}

export const uploadDocumentViaLink = async (token: string, file: File): Promise<MatterDocument> => {
  const response = await fetch(`${API_BASE_URL}/matter-upload-links/${token}/documents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name, contentType: file.type || 'application/octet-stream' }),
  })
  const data = await response.json()

  if (!response.ok) {
    throw new Error(data?.error || `Failed to start upload (${response.status})`)
  }

  await uploadFileToS3(data.uploadUrl, file)

  return data.document
}
